import { useEffect, useReducer, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { GarminAiAttribution } from '../components/GarminAttribution.tsx';
import { useChat, type ChatStateBinding } from '../hooks/useChat.ts';
import type { ChatMessage } from '../lib/store.ts';

const FUEL_LOADING_KEYS = [
  'fuel.loading1',
  'fuel.loading2',
  'fuel.loading3',
] as const;

const FUEL_EXAMPLE_KEYS = [
  'fuel.example1',
  'fuel.example2',
  'fuel.example3',
] as const;

/**
 * Fuel — the sports-nutrition coach product surface.
 *
 * Deliberately simpler than the cycling Chat page: no conversation
 * sidebar, no clear-chat button, no DB-persisted history. The MVP is
 * single-session Q&A (idea doc Phase 1). Multi-turn within a session
 * works via the in-memory ``session_id`` on the backend.
 *
 * Chat state is kept in local React state — NOT in the global Zustand
 * store — so navigating between ``/`` (cycling chat) and ``/fuel`` does
 * not mix the two conversations.
 */
export function Fuel() {
  const { t } = useTranslation();
  // History and sessionId are kept in BOTH a mutable ref AND React
  // state. The ref is the synchronous source of truth that ``useChat``'s
  // streaming callbacks read between rapid token events; React state
  // drives renders. Reading via the ref avoids stale-state bugs when
  // two setState calls happen back-to-back in the same task (e.g.
  // ``addMessage`` for the first token followed by ``updateLastMessage``
  // for the second — by the time the second runs, React hasn't flushed
  // the first yet).
  const historyRef = useRef<ChatMessage[]>([]);
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const sessionIdRef = useRef<string | null>(null);
  // Re-render trigger only; the live value is read from ``sessionIdRef``.
  // The reducer just stores the new id, so (like a plain setter) an
  // unchanged id bails out without re-rendering.
  const [, setSessionIdState] = useReducer(
    (_prev: string | null, id: string | null) => id,
    null,
  );

  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // Lazy useState init: build the binding object exactly once. The
  // object itself is stable; live data flows through the getters which
  // reference the refs above.
  const [binding] = useState<ChatStateBinding>(() => {
    const writeHistory = (next: ChatMessage[]) => {
      historyRef.current = next;
      setHistory(next);
    };
    return {
      getHistory: () => historyRef.current,
      addMessage: (msg) => writeHistory([...historyRef.current, msg]),
      updateLastMessage: (content) => {
        const cur = historyRef.current;
        const last = cur.at(-1);
        if (!last) return;
        writeHistory([...cur.slice(0, -1), { ...last, content }]);
      },
      setLastSources: (sources) => {
        const cur = historyRef.current;
        const last = cur.at(-1);
        if (last?.role !== 'coach') return;
        writeHistory([...cur.slice(0, -1), { ...last, aiSources: sources }]);
      },
      getSessionId: () => sessionIdRef.current,
      setSessionId: (id) => {
        sessionIdRef.current = id;
        setSessionIdState(id);
      },
      // No DB-persisted conversation on /fuel for MVP.
      getConversationId: () => null,
      setConversationId: () => {},
    };
  });

  const { send, isStreaming, loadingText } = useChat({
    endpoint: '/nutrition/chat/stream',
    loadingKeys: FUEL_LOADING_KEYS,
    warmUpKey: 'fuel.warmingUp',
    errorKey: 'fuel.errorSend',
    state: binding,
  });

  // Smooth scroll to bottom as tokens arrive.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, isStreaming]);

  // Update tab title while streaming.
  useEffect(() => {
    if (isStreaming) {
      document.title = `\u{1F33F} ${t('fuel.typing')}`;
    } else {
      document.title = t('fuel.title');
    }
    return () => {
      document.title = t('fuel.title');
    };
  }, [isStreaming, t]);

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    const msg = input.trim();
    if (!msg || isStreaming) return;
    setInput('');
    send(msg);
  };

  const handleChipClick = (text: string) => {
    if (isStreaming) return;
    setInput(text);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-(--color-border) bg-(--color-bg-secondary)">
        <span className="text-xl">{'\u{1F33F}'}</span>
        <h1 className="text-lg font-bold text-emerald-400">{t('fuel.title')}</h1>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {history.length === 0 && !isStreaming && (
          <div className="flex flex-col items-center justify-center h-full text-center text-(--color-text-secondary)">
            <span className="text-6xl mb-4">{'\u{1F33F}'}</span>
            <h2 className="text-xl font-semibold text-(--color-text-primary) mb-2">
              {t('fuel.welcome')}
            </h2>
            <p className="max-w-md mb-6">{t('fuel.welcomeSub')}</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-xl">
              {FUEL_EXAMPLE_KEYS.map((k) => {
                const text = t(k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => handleChipClick(text)}
                    className="text-xs px-3 py-2 rounded-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors"
                  >
                    {text}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {history.map((msg, i) => {
          const isLastCoach =
            msg.role === 'coach' && i === history.length - 1 && isStreaming;
          return (
            <div
              key={`${msg.role}-${i}`}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? 'bg-emerald-400 text-gray-900 rounded-br-md'
                    : 'bg-(--color-bg-tertiary) text-(--color-text-primary) rounded-bl-md'
                }`}
              >
                {msg.role === 'coach' && (
                  <span className="text-base mr-1">{'\u{1F33F}'}</span>
                )}
                {msg.content}
                {isLastCoach && (
                  <span className="inline-block w-0.5 h-4 bg-emerald-400 ml-0.5 align-middle animate-pulse" />
                )}
                {msg.role === 'coach' && !isLastCoach && (
                  <GarminAiAttribution sources={msg.aiSources} className="mt-2" />
                )}
              </div>
            </div>
          );
        })}
        {isStreaming && loadingText && (
          <div className="flex justify-start">
            <div className="bg-(--color-bg-tertiary) px-4 py-3 rounded-2xl rounded-bl-md text-sm text-(--color-text-secondary)">
              {loadingText}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="px-4 py-3 border-t border-(--color-border) bg-(--color-bg-secondary)"
      >
        <div className="flex gap-2 max-w-3xl mx-auto">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              isStreaming ? t('fuel.responding') : t('fuel.placeholder')
            }
            disabled={isStreaming}
            className="flex-1 bg-(--color-bg-tertiary) text-(--color-text-primary) placeholder-(--color-text-secondary) rounded-xl px-4 py-3 text-sm border border-(--color-border) focus:outline-none focus:border-emerald-400 transition-colors disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={isStreaming || !input.trim()}
            className="bg-emerald-400 text-gray-900 px-5 py-3 rounded-xl font-semibold text-sm hover:bg-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {t('fuel.send')}
          </button>
        </div>
      </form>
    </div>
  );
}
